import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Spinner, Badge, Alert, Nav, Tab } from 'react-bootstrap';
import {
  fetchProfile,
  updateUserProfile,
  uploadAvatar,
  fetchEarnings,
  fetchMyLearning,
  fetchLanguages,
  clearUpdateSuccess,
} from '../../store/slices/profileSlice';
import { fetchMyBookings } from '../../store/slices/classesSlice';
import { getAllCountriesApi } from '../../api/auth';
import DummyUser from '../../assets/images/avatar.png';
import { BlueVerificationTab } from './BlueVerificationTab';
import { SubscriptionsTab } from './SubscriptionsTab';
import { TeacherScreeningTab } from './TeacherScreeningTab';
import './user-profile.scss';

// ── Sub-components ──────────────────────────────

const ProfileInfoTab = ({ profile, languages, loading, error, updateSuccess, onSubmit, dispatch, countries = [] }) => {
  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    mobile: profile?.mobile || '',
    country: profile?.country?._id || profile?.country || '',
    description: profile?.description || '',
    experience: profile?.experience || '',
    time_zone: profile?.time_zone || '',
  });

  useEffect(() => {
    setForm({
      full_name: profile?.full_name || '',
      mobile: profile?.mobile || '',
      country: profile?.country?._id || profile?.country || '',
      description: profile?.description || '',
      experience: profile?.experience || '',
      time_zone: profile?.time_zone || '',
    });
  }, [profile]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="profile-info-tab">
      {updateSuccess && (
        <Alert variant="success" className="rounded-3 mb-4 d-flex align-items-center gap-2" onClose={() => dispatch(clearUpdateSuccess())} dismissible>
          <i className="bi bi-check-circle-fill fs-5"></i>
          <span>Profile updated successfully!</span>
        </Alert>
      )}
      {error?.update && <Alert variant="danger" className="rounded-3 mb-4">{error.update}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold text-gray-700">Full Name</Form.Label>
              <Form.Control
                name="full_name" value={form.full_name} onChange={handleChange}
                placeholder="Your full name" className="profile-input py-3 rounded-3"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold text-gray-700">Mobile</Form.Label>
              <Form.Control
                name="mobile" value={form.mobile} onChange={handleChange}
                placeholder="+1 234 567 890" className="profile-input py-3 rounded-3"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold text-gray-700">Country</Form.Label>
              <Form.Select
                name="country" value={form.country} onChange={handleChange}
                className="profile-input py-3 rounded-3"
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          {profile?.user_type === 'teacher' && (
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-gray-700">Years of Experience</Form.Label>
                <Form.Control
                  type="number" min="0" name="experience" value={form.experience} onChange={handleChange}
                  placeholder="e.g. 5" className="profile-input py-3 rounded-3"
                />
              </Form.Group>
            </Col>
          )}
          <Col md={12}>
            <Form.Group>
              <Form.Label className="fw-semibold text-gray-700">Bio / Description</Form.Label>
              <Form.Control
                as="textarea" rows={4} name="description" value={form.description} onChange={handleChange}
                placeholder="Tell learners about yourself..." className="profile-input rounded-3"
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Button type="submit" className="btn-save px-5 py-2 fw-bold rounded-3" disabled={loading.update}>
              {loading.update
                ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</>
                : <><i className="bi bi-check2 me-2"></i>Save Changes</>
              }
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const MyClassesTab = ({ myBookings, loading }) => (
  <div>
    {loading.myBookings && (
      <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
    )}
    {!loading.myBookings && myBookings.length === 0 && (
      <div className="empty-tab-state">
        <i className="bi bi-camera-video-off"></i>
        <h5>No classes booked yet</h5>
        <p>Explore our classes and book your first session!</p>
        <a href="/classes" className="btn btn-primary rounded-pill px-4">Browse Classes</a>
      </div>
    )}
    {myBookings.map((cls) => (
      <div key={cls._id} className="booking-item d-flex align-items-center gap-3 p-3 mb-3 rounded-3 border bg-white">
        <div className="booking-icon bg-primary bg-opacity-10 rounded-3 flex-center" style={{ width: '48px', height: '48px' }}>
          <i className="bi bi-camera-video-fill text-primary fs-5"></i>
        </div>
        <div className="flex-grow-1">
          <div className="fw-semibold text-gray-900">{cls.title || 'Class Session'}</div>
          <div className="text-sm text-gray-500">
            <i className="bi bi-calendar3 me-1"></i>
            {cls.start_date ? new Date(cls.start_date).toLocaleDateString() : 'TBD'}
            {cls.start_time && ` · ${cls.start_time}`}
          </div>
        </div>
        <Badge bg={cls.type === 'one_to_one' ? 'success' : 'primary'} className="rounded-pill">
          {cls.type === 'one_to_one' ? '1-on-1' : 'Group'}
        </Badge>
      </div>
    ))}
  </div>
);

const MyLearningTab = ({ myLearning, loading }) => {
  const getProgressPercent = (progress) => {
    if (!progress) return 0;
    if (typeof progress === 'number') return progress;
    if (typeof progress === 'object') {
      const total = progress.total_no_of_lesson || 0;
      const completed = progress.no_of_completed_lesson || 0;
      return total > 0 ? Math.round((completed / total) * 100) : 0;
    }
    return 0;
  };

  return (
    <div>
      {loading.myLearning && (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      )}
      {!loading.myLearning && myLearning.length === 0 && (
        <div className="empty-tab-state">
          <i className="bi bi-book-half"></i>
          <h5>You haven't enrolled in any courses</h5>
          <p>Start your language learning journey today!</p>
          <a href="/courses" className="btn btn-primary rounded-pill px-4">Browse Courses</a>
        </div>
      )}
      <Row className="g-3">
        {myLearning.map((course, i) => {
          const progressPercent = getProgressPercent(course.progress);
          return (
            <Col md={6} key={course._id || i}>
              <div className="learning-item d-flex align-items-center gap-3 p-3 rounded-3 border bg-white h-100">
                <img
                  src={course.cover_image || course.image || 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=80&h=80&fit=crop'}
                  alt={course.title}
                  className="rounded-3 object-fit-cover"
                  style={{ width: '64px', height: '64px' }}
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=80&h=80&fit=crop'; }}
                />
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-semibold text-gray-900 text-truncate">{course.title || 'Course'}</div>
                  <div className="text-sm text-gray-500 text-truncate">{course.instructor?.full_name || ''}</div>
                  {course.progress !== undefined && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between text-xs text-gray-500 mb-1">
                        <span>Progress</span><span>{progressPercent}%</span>
                      </div>
                      <div className="progress" style={{ height: '4px' }}>
                        <div className="progress-bar bg-primary" style={{ width: `${progressPercent}%` }}></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

// ── Main Component ───────────────────────────────

export const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const { profile, languages, earnings, myLearning, loading, error, updateSuccess } = useSelector((s) => s.profile);
  const { myBookings, loading: classLoading } = useSelector((s) => s.classes);
  const location = useLocation();
  const query = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabParam = query.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'info');

  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileRef = useRef(null);

  const [countries, setCountries] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProfile());
      dispatch(fetchLanguages());
      dispatch(fetchMyBookings());
      dispatch(fetchMyLearning());

      getAllCountriesApi()
        .then((res) => {
          if (res && res.data) {
            setCountries(res.data);
          } else if (Array.isArray(res)) {
            setCountries(res);
          }
        })
        .catch((err) => console.error('Failed to load countries:', err));
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && profile?.user_type === 'teacher') {
      dispatch(fetchEarnings());
    }
  }, [profile?.user_type, dispatch, isAuthenticated]);

  if (!isAuthenticated) return <Navigate to="/login" />;

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append('image', file);
    dispatch(uploadAvatar(fd));
  };

  const handleProfileUpdate = (data) => {
    dispatch(updateUserProfile(data));
  };

  const isTeacher = profile?.user_type === 'teacher' || user?.user_type === 'teacher';
  const displayPic = avatarPreview || profile?.profile_picture || user?.profile_picture || DummyUser;
  const displayName = profile?.full_name || user?.full_name || 'User';

  const tabs = [
    { key: 'info', label: 'Profile Info', icon: 'bi-person-fill' },
    { key: 'classes', label: 'My Classes', icon: 'bi-camera-video-fill' },
    { key: 'courses', label: 'My Courses', icon: 'bi-book-fill' },
    ...(isTeacher ? [
      { key: 'revenue', label: 'Earnings', icon: 'bi-cash-stack' },
      { key: 'screening', label: 'Teacher Screening', icon: 'bi-journal-check' }
    ] : []),
    { key: 'verification', label: 'Blue Tick', icon: 'bi-patch-check-fill' },
    { key: 'subscriptions', label: 'Subscriptions', icon: 'bi-credit-card-2-front-fill' },
    { key: 'settings', label: 'Settings', icon: 'bi-gear-fill' },
  ];

  return (
    <section className="user-profile-page py-5">
      <Container fluid="xl">
        {/* Profile Header */}
        <div className="profile-header-card mb-5">
          <div className="profile-header-bg"></div>
          <div className="profile-header-content p-4 p-md-5">
            <div className="d-flex flex-column flex-md-row align-items-center align-items-md-end gap-4">
              {/* Avatar */}
              <div className="avatar-wrap position-relative">
                <img
                  src={displayPic}
                  alt={displayName}
                  className="profile-avatar rounded-circle object-fit-cover border border-4 border-white shadow-lg"
                  style={{ width: '100px', height: '100px' }}
                  onError={(e) => { e.target.src = DummyUser; }}
                />
                <button
                  className="avatar-edit-btn position-absolute bottom-0 end-0 btn btn-primary rounded-circle shadow"
                  onClick={() => fileRef.current?.click()}
                  disabled={loading.avatar}
                  title="Change photo"
                >
                  {loading.avatar
                    ? <Spinner animation="border" size="sm" />
                    : <i className="bi bi-camera-fill"></i>
                  }
                </button>
                <input
                  ref={fileRef} type="file" accept="image/*"
                  className="d-none" onChange={handleAvatarChange}
                />
              </div>

              {/* Info */}
              <div className="flex-grow-1 text-center text-md-start">
                {loading.profile
                  ? <Spinner animation="border" variant="primary" />
                  : (
                    <>
                      <h1 className="profile-name fw-black text-white mb-1">{displayName}</h1>
                      <div className="d-flex flex-wrap justify-content-center justify-content-md-start align-items-center gap-2">
                        <Badge className="role-badge rounded-pill px-3 py-2" bg={isTeacher ? 'warning' : 'info'}>
                          <i className={`bi ${isTeacher ? 'bi-mortarboard-fill' : 'bi-person-fill'} me-1`}></i>
                          {isTeacher ? 'Teacher' : 'Student'}
                        </Badge>
                        {profile?.country && (
                          <span className="text-white text-opacity-80 text-sm">
                            <i className="bi bi-geo-alt-fill me-1"></i>
                            {typeof profile.country === 'object' ? profile.country.name : profile.country}
                          </span>
                        )}
                        {profile?.is_blue_verified && (
                          <Badge bg="info" className="rounded-pill px-3 py-2">
                            <i className="bi bi-patch-check-fill me-1"></i>Verified
                          </Badge>
                        )}
                      </div>
                    </>
                  )
                }
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => k === 'revenue' ? navigate('/revenue') : k === 'settings' ? navigate('/settings') : setActiveTab(k)}>
          <div className="profile-tabs-wrapper mb-4">
            <Nav className="profile-tabs gap-1">
              {tabs.map((t) => (
                <Nav.Item key={t.key}>
                  <Nav.Link eventKey={t.key} className="profile-tab-link rounded-pill px-4 py-2">
                    <i className={`bi ${t.icon} me-2`}></i>{t.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>

          <div className="profile-tab-content">
            <Tab.Content>
              <Tab.Pane eventKey="info">
                <ProfileInfoTab
                  profile={profile}
                  languages={languages}
                  loading={loading}
                  error={error}
                  updateSuccess={updateSuccess}
                  onSubmit={handleProfileUpdate}
                  dispatch={dispatch}
                  countries={countries}
                />
              </Tab.Pane>
              <Tab.Pane eventKey="classes">
                <MyClassesTab myBookings={myBookings} loading={classLoading} />
              </Tab.Pane>
              <Tab.Pane eventKey="courses">
                <MyLearningTab myLearning={myLearning} loading={loading} />
              </Tab.Pane>
              <Tab.Pane eventKey="verification">
                <BlueVerificationTab />
              </Tab.Pane>
              <Tab.Pane eventKey="subscriptions">
                <SubscriptionsTab />
              </Tab.Pane>
              {isTeacher && (
                <Tab.Pane eventKey="screening">
                  <TeacherScreeningTab />
                </Tab.Pane>
              )}
            </Tab.Content>
          </div>
        </Tab.Container>
      </Container>
    </section>
  );
};
